/**
 * Stage Plot — natural-language placement assistant (LOT C).
 *
 * A panel in the stage-plot editor: the user types a placement command
 * ("put the keyboards stage-left on the riser") and the AI PROPOSES structured
 * actions. Nothing is applied until the user clicks Apply — and even then it
 * only touches the local editor store (undoable); the normal editor Save
 * persists the plot. There is NO server write route.
 *
 * Offline: the input is disabled with a clear message (the editor itself keeps
 * working offline).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiAiStageplotCommand, apiAiCredits, type StageCommandAction } from '../../api';
import { describeAction } from '../../lib/stageCommand';
import { getOfflineState, subscribeOffline } from '../../lib/offline';
import type { StageElement, StageDimensions } from '../../shared/types/stageplot';

interface AssistantProps {
  bandId: string;
  elements: StageElement[];
  stageDimensions: StageDimensions;
  /** Apply validated actions to the editor store (undoable). */
  onApply: (actions: StageCommandAction[]) => void;
  /** content.rider write gate — the assistant is read-only, but Apply is a write. */
  canWrite: boolean;
}

interface HistoryEntry {
  id: string;
  reply: string;
  actions: StageCommandAction[];
  rejected: unknown[];
  applied: boolean;
}

const SUGGESTIONS = [
  'Put the keyboards stage-left on the riser',
  'Add a wedge in front of the drummer',
  'Remove the second guitar stand',
];

export default function StagePlotAssistant({
  bandId,
  elements,
  stageDimensions,
  onApply,
  canWrite,
}: AssistantProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [unlimited, setUnlimited] = useState(false);
  const [offline, setOffline] = useState(getOfflineState().offline);
  const [justApplied, setJustApplied] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    apiAiCredits().then((d) => { setCredits(d.credits); setUnlimited(!!d.unlimited); }).catch(() => setCredits(null));
  }, []);

  useEffect(() => subscribeOffline((s) => setOffline(s.offline)), []);

  const outOfCredits = !unlimited && credits !== null && credits <= 0;
  const disabled = thinking || offline || outOfCredits || !canWrite;

  const send = useCallback(async () => {
    const message = input.trim();
    if (!message || thinking || offline || outOfCredits || !canWrite) return;
    setThinking(true);
    setError(null);
    setJustApplied(false);
    try {
      const result = await apiAiStageplotCommand({
        bandId,
        message,
        stage: {
          width: stageDimensions.width,
          height: stageDimensions.height,
          unit: stageDimensions.unit,
        },
        elements: elements.map((e) => ({
          id: e.id,
          label: e.label,
          iconId: e.iconId,
          x: e.x,
          y: e.y,
          rotation: e.rotation,
        })),
        iconIds: [],
      });
      setCredits(result.remaining);
      setUnlimited(!!result.unlimited);
      setHistory((prev) => [
        {
          id: `h-${(seq.current += 1)}`,
          reply: result.reply,
          actions: result.actions,
          rejected: result.rejected,
          applied: false,
        },
        ...prev,
      ]);
      setInput('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Assistant request failed');
    } finally {
      setThinking(false);
    }
  }, [input, thinking, offline, outOfCredits, canWrite, bandId, stageDimensions, elements]);

  const applyAll = useCallback(
    (entryId: string, actions: StageCommandAction[]) => {
      if (!canWrite) return;
      onApply(actions);
      setHistory((prev) => prev.map((h) => (h.id === entryId ? { ...h, applied: true } : h)));
      setJustApplied(true);
    },
    [onApply, canWrite],
  );

  const applyOne = useCallback(
    (entryId: string, action: StageCommandAction) => {
      if (!canWrite) return;
      onApply([action]);
      setHistory((prev) => prev.map((h) => (h.id === entryId ? { ...h, applied: true } : h)));
      setJustApplied(true);
    },
    [onApply, canWrite],
  );

  return (
    <div className="stageplot-assistant" data-stageplot-assistant>
      <div className="stageplot-assistant-header">
        <span className="stageplot-assistant-title">{t('stagePlotEditor.assistantTitle')}</span>
        <span className="stageplot-ai-credits-badge">
          {unlimited ? '∞' : `${credits ?? '…'} ${t('stagePlotEditor.credits')}`}
        </span>
      </div>

      {offline && <p className="stageplot-ai-hint">{t('stagePlotEditor.assistantOffline')}</p>}
      {outOfCredits && <p className="stageplot-ai-credits-out">{t('stagePlotEditor.outOfCredits')}</p>}
      {!canWrite && <p className="stageplot-ai-hint">{t('stagePlotEditor.assistantReadOnly')}</p>}

      <div className="stageplot-assistant-input-row">
        <input
          type="text"
          className="stageplot-assistant-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void send(); }}
          placeholder={t('stagePlotEditor.assistantPh')}
          aria-label={t('stagePlotEditor.assistantPh')}
          disabled={disabled}
        />
        <button className="btn btn-primary btn-small" onClick={() => void send()} disabled={disabled}>
          {thinking ? t('stagePlotEditor.assistantThinking') : t('stagePlotEditor.assistantSend')}
        </button>
      </div>

      {error && <div className="stageplot-error"><p>⚠️ {error}</p></div>}
      {justApplied && (
        <p className="stageplot-assistant-applied" role="status">
          {t('stagePlotEditor.assistantApplied')}
        </p>
      )}

      {history.length === 0 && !thinking && (
        <div className="stageplot-assistant-suggestions">
          <p className="stageplot-ai-hint">{t('stagePlotEditor.assistantSuggestHint')}</p>
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              className="stageplot-assistant-suggestion"
              onClick={() => setInput(s)}
              disabled={disabled}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <ul className="stageplot-assistant-history">
          {history.map((h) => (
            <li key={h.id} className="stageplot-assistant-entry">
              <p className="stageplot-assistant-reply">{h.reply}</p>
              {h.actions.length === 0 ? (
                <p className="stageplot-ai-hint">{t('stagePlotEditor.assistantNoActions')}</p>
              ) : (
                <ul className="stageplot-assistant-actions">
                  {h.actions.map((a, i) => (
                    <li key={i} className="stageplot-assistant-action">
                      <span className="stageplot-assistant-action-text">{describeAction(a)}</span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-small"
                        onClick={() => applyOne(h.id, a)}
                        disabled={!canWrite || h.applied}
                      >
                        {t('stagePlotEditor.assistantApply')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {h.rejected.length > 0 && (
                <p className="stageplot-ai-hint">{t('stagePlotEditor.assistantRejected', { count: h.rejected.length })}</p>
              )}
              {h.actions.length > 0 && (
                <div className="stageplot-assistant-entry-actions">
                  <button
                    type="button"
                    className="btn btn-primary btn-small"
                    onClick={() => applyAll(h.id, h.actions)}
                    disabled={!canWrite || h.applied}
                  >
                    {t('stagePlotEditor.assistantApplyAll')}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
